import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useErrorHandler } from '../../errorHandler';
import GoogleMapComponent from './GoogleMap';
import MapSearchBox from './MapSearchBox';
import MapLegend from './MapLegend';
import MapTypeSelector from './MapTypeSelector';
import MapZoomSelector from './MapZoomSelector';
import useMap from './useMap';
import useFlightPaths from './useFlightPaths';
import useGeoFences from './useGeoFences';
import useNoFlyZones from './useNoFlyZones';
import useSafePorts from './useSafePorts';
import useWaypoints from './useWaypoints';
import useRadar from './useRadar';
import usePaths from './usePaths';
import { writeMonitorMapHandoff, zoomToRoughAltitudeMeters } from '../monitorMapHandoff';
// 3D遷移時のみ使用するフォールバック座標（geolocation初期センターには使用しない）
const FALLBACK_CENTER = { lat: 35.465, lng: 139.76 };
function MapComponent({ apiKey, geoFences, noFlyZones, safePorts, flightPaths, waypoints, radar, paths, locked = false, draggable = false, gestureHandling, hideAllUIs = false, hideSearchBar = false, hideAreaCenter = false, highlightPath = false, onDragEnd, onClick, on3dSelected, interactiveLegend = false, children, }) {
    const theme = useTheme();
    const { bounds, center, setCenter, zoom } = useMap();
    const { showError } = useErrorHandler();
    const [geoCenter, setGeoCenter] = React.useState();
    // geolocation が成功/失敗/タイムアウトのいずれかで決着したか。
    // ブラウザは許可プロンプトを放置されると成功・失敗コールバックをどちらも呼ばないため、
    // 安全タイマーで必ず settled にして地図初期化を保証する。
    const [geoSettled, setGeoSettled] = React.useState(false);
    const { markers: flightPathMarkers } = useFlightPaths(flightPaths, !locked);
    const { markers: geoFenceMarkers } = useGeoFences(geoFences, hideAreaCenter, !locked, onDragEnd);
    const { markers: noFlyZoneMarkers } = useNoFlyZones(noFlyZones, hideAreaCenter, !locked, onDragEnd);
    const { markers: safePortMarkers } = useSafePorts(safePorts, !locked, onDragEnd);
    const { markers: waypointsMarkers } = useWaypoints(waypoints, !locked, onDragEnd);
    const {} = useRadar(radar);
    const {} = usePaths(paths, highlightPath);
    const handle3dNavigate = React.useCallback(() => {
        if (!on3dSelected) {
            return;
        }
        const lat = typeof center?.lat === 'number' ? center.lat : FALLBACK_CENTER.lat;
        const lng = typeof center?.lng === 'number' ? center.lng : FALLBACK_CENTER.lng;
        const mapZoom = typeof zoom === 'number' && zoom > 0 ? zoom : 14;
        writeMonitorMapHandoff({
            latitude: lat,
            longitude: lng,
            zoom: mapZoom,
            altitudeMeters: zoomToRoughAltitudeMeters(mapZoom, lat),
        });
        on3dSelected();
    }, [center?.lat, center?.lng, on3dSelected, zoom]);
    // 端末の現在地は最優先: マウント時にすぐ取得を開始する(データ由来センターより先に飛ばないよう
    // 遅延せず要求し、取得できたら後続の effect で適用する)
    React.useEffect(() => {
        if (!navigator.geolocation) {
            setGeoSettled(true);
            return;
        }
        let cancelled = false;
        // 許可プロンプト放置などでコールバックが一切来ないケースへの安全網。
        // settled になればデータ由来センターかフォールバックへ進める。
        const settleTimer = window.setTimeout(() => {
            if (!cancelled) {
                setGeoSettled(true);
            }
        }, 4000);
        navigator.geolocation.getCurrentPosition((position) => {
            if (cancelled) {
                return;
            }
            window.clearTimeout(settleTimer);
            setGeoCenter({ lat: position.coords.latitude, lng: position.coords.longitude });
            setGeoSettled(true);
        }, () => {
            if (cancelled) {
                return;
            }
            window.clearTimeout(settleTimer);
            showError(new Error('Unable to retrieve your location'));
            setGeoSettled(true);
        }, { maximumAge: Infinity, enableHighAccuracy: true, timeout: 10000 });
        return () => {
            cancelled = true;
            window.clearTimeout(settleTimer);
        };
    }, [showError]);
    // データ由来センターはフォールバック: 現在地が未取得で、かつ center/bounds 未設定の時だけ適用
    React.useEffect(() => {
        if (bounds || center || !setCenter || geoCenter) {
            return;
        }
        const waypoint = waypoints?.at(0)?.location;
        if (waypoint) {
            console.debug('center waypoint');
            setCenter(waypoint);
            return;
        }
        const flightPath = flightPaths?.at(0)?.waypoints?.at(0)?.location;
        if (flightPath) {
            console.debug('center flight path');
            setCenter(flightPath);
            return;
        }
        const safePort = safePorts?.at(0)?.location;
        if (safePort) {
            console.debug('center safe port');
            setCenter(safePort);
            return;
        }
        const geoFence = geoFences?.at(0)?.area?.at(0)?.at(0);
        if (geoFence) {
            console.debug('center geo fence');
            setCenter({ lat: geoFence[1], lng: geoFence[0] });
            return;
        }
        const path = paths?.at(0)?.at(0);
        if (path) {
            console.debug('center path');
            setCenter({ lat: path.lat, lng: path.lng });
            return;
        }
        // データ由来の座標が一切無い場合のみ、geolocation の決着を待ってから
        // フォールバック地点をセットする(セットしないと GoogleMap が永久に Loading のまま)。
        // geoSettled は安全タイマーで必ず true になるので地図は必ず初期化される。
        if (geoSettled) {
            console.debug('center fallback');
            setCenter(FALLBACK_CENTER);
        }
    }, [center, waypoints, flightPaths, geoFences, safePorts, noFlyZones, paths, setCenter, bounds, geoCenter, geoSettled]);
    React.useEffect(() => {
        if (!geoCenter || !setCenter) {
            return;
        }
        if ((bounds?.length || 0) > 0) {
            return;
        }
        console.debug('center geolocation');
        setCenter(geoCenter);
    }, [geoCenter, bounds, setCenter]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Box, { sx: {
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                '.gm-style-iw': {
                    backgroundColor: theme.palette.background.paper,
                    paddingRight: '12px !important',
                    paddingBottom: '12px !important',
                },
                '.gm-style-iw-d': {
                    overflow: 'auto !important',
                },
                '.gm-style-iw-tc::after': {
                    // For the arrow at the bottom of an info window.
                    backgroundColor: theme.palette.background.paper,
                },
                'button.gm-ui-hover-effect > span': {
                    // For the close icon at the top right corner in an info window.
                    backgroundColor: theme.palette.text.primary,
                },
            } },
            React.createElement(GoogleMapComponent, { apiKey, locked, draggable, gestureHandling, onClick },
                flightPathMarkers,
                waypointsMarkers,
                geoFenceMarkers,
                noFlyZoneMarkers,
                safePortMarkers,
                children)),
        !hideAllUIs ? (React.createElement(Box, { gap: 1, sx: { position: 'absolute' } },
            React.createElement(MapTypeSelector, { on3dSelected: on3dSelected ? handle3dNavigate : undefined }))) : null,
        !hideAllUIs ? (React.createElement(Box, { sx: { position: 'absolute', bottom: 2 } },
            React.createElement(MapLegend, { enableRadar: interactiveLegend || !!radar, interactive: interactiveLegend }))) : null,
        !hideAllUIs && !hideSearchBar ? (React.createElement(Box, { sx: {
                position: 'absolute',
                left: '50%',
                width: '50%',
                transform: 'translateX(-50%)',
                textAlign: 'center',
                maxWidth: 600,
            } },
            React.createElement(MapSearchBox, null))) : null,
        !hideAllUIs ? (React.createElement(Box, { sx: { position: 'absolute', bottom: 14, right: 1 } },
            React.createElement(MapZoomSelector, null))) : null));
}
export default React.memo(MapComponent);
